import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { backendService } from "../services/backend.service";
import type { Nurse } from "../utils/models";
import { NURSES_QUERY_KEY_ROOT } from "./nursesQueryKeys";

// HospitalDataProvider refetches this same entry after every roster write and patches it after a time-off
// change, so a page mounting shortly after doesn't need its own request to be current.
const ROSTER_STALE_TIME_MS = 5 * 60 * 1000;

const nursesQueryOptions = (departmentId: string | undefined, activeOnDate: string | undefined) => ({
	queryKey: [NURSES_QUERY_KEY_ROOT, departmentId, activeOnDate],
	queryFn: async () => {
		const response = await backendService.getNurses(departmentId, activeOnDate);
		return response.data;
	},
});

export const departmentRosterQueryOptions = (departmentId: string) => ({
	...nursesQueryOptions(departmentId, undefined),
	staleTime: ROSTER_STALE_TIME_MS,
});

/** The department roster query, shared by every hook that reads the whole roster so they hit one cache entry. */
export const useDepartmentRosterQuery = (departmentId: string) => useQuery<Nurse[]>(departmentRosterQueryOptions(departmentId));

/** One nurse from the department roster, re-rendering only when that nurse changes — for rows that each show one person. */
export const useRosterNurse = (departmentId: string, nurseId: number): Nurse | undefined => {
	const select = useCallback((roster: Nurse[]) => roster.find((nurse) => nurse.id === nurseId), [nurseId]);
	return useQuery<Nurse[], Error, Nurse | undefined>({ ...departmentRosterQueryOptions(departmentId), select }).data;
};

export const useNurses = (departmentId?: string, activeOnDate?: string) => {
	return useQuery<Nurse[]>({
		...nursesQueryOptions(departmentId, activeOnDate),
		enabled: false,
	});
};
